import React, { useEffect, useRef, useState } from "react";
import {
  Grid,
  Typography,
  makeStyles,
  withStyles,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  InputLabel,
  Paper,
  Chip,
  Box,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  getAttribute,
  getAttributeValue,
} from "../../../../Utilities/Functions";
import StepActions from "./stepActions";
import styles from "./styles";
import { Markup } from "interweave";
import { getCaptchaChallenge } from "../../../../API/Entity/DocumentRequest";

const ReviewRequestStep = (props) => {
  const { entity, request, canSkip, onCancel, onSubmit, onBack } = props;
  const { t } = useTranslation();
  const useStyles = makeStyles(styles);
  const classes = useStyles();
  const [altchaPayload, setAltchaPayload] = useState(null);
  const [altchaLoading, setAltchaLoading] = useState(true);
  const widgetRef = useRef(null);

  const handleCancel = (event) => {
    onCancel();
  };

  const handleBack = (event) => {
    onBack();
  };

  const handleSubmit = (event) => {
    onSubmit({ challengeResponse: altchaPayload });
  };

  let captchaMounted = true;

  useEffect(() => {
    const setupWidget = async () => {
      try {
        setAltchaLoading(true);
        const responseBuffer = await getCaptchaChallenge();
        const decoder = new TextDecoder("utf-8");
        const challenge = JSON.parse(decoder.decode(responseBuffer));

        if (captchaMounted && widgetRef.current) {
          widgetRef.current.configure({ challenge });
        }
      } catch (err) {
        console.error("Failed to load challenge");
      } finally {
        if (captchaMounted) setAltchaLoading(false);
      }
    };

    const handleWidgetStateChange = (event) => {
      if (event.detail.state === "verified") {
        setAltchaPayload(event.detail.payload);
      } else if (
        event.detail.state === "expired" ||
        event.detail.state === "error"
      ) {
        setAltchaPayload(null);
      }
    };

    const currentWidget = widgetRef.current;

    if (currentWidget) {
      currentWidget.addEventListener("statechange", handleWidgetStateChange);
    }

    setupWidget();

    return () => {
      captchaMounted = false;
      if (currentWidget) {
        currentWidget.removeEventListener(
          "statechange",
          handleWidgetStateChange,
        );
      }
    };
  }, []);

  const getRecordName = (record) => {
    const { entity, associations } = record;
    let name = getAttributeValue(getAttribute(entity.attributes, "3"));
    if (associations && associations.length > 0) {
      const associationNames = associations
        .map((a) => a.associationTypeName)
        .sort();
      return (
        <span>
          <Link to={`/basicinfo?id=${entity.id}`} target="_blank">
            {name}
          </Link>
          <Typography component="span">
            {`(${Array.from(new Set(associationNames)).join(",")})`}
          </Typography>
        </span>
      );
    }
    return name;
  };

  const renderHeader = () => {
    const { requestType, records = [] } = request;
    const client = records.find((r) => r.entity.id === entity.id);
    if (!client) {
      return;
    }
    const { clientType = {} } = client || {};
    const clientName = getRecordName(client);

    return (
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.requestType")}
              </InputLabel>
              <Typography>
                {requestType === "1"
                  ? t(
                      "labels.record.documentRequest.steps.requestTypeStep.genericRequest",
                    )
                  : t(
                      "labels.record.documentRequest.steps.requestTypeStep.specificRequest",
                    )}
              </Typography>
            </Grid>
            <Grid item xs={10}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.clientName")}
              </InputLabel>
              <Typography>{clientName}</Typography>
            </Grid>
            <Grid item xs={2}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.clientType")}
              </InputLabel>
              <Typography>{clientType.name}</Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderAssociatedRecords = () => {
    const { records = [] } = request;
    const associatedRecords = records.filter((r) => r.entity.id !== entity.id);
    if (associatedRecords.length === 0) {
      return;
    }
    return (
      <Card variant="outlined">
        <CardHeader subheader={"Associated Records"}></CardHeader>
        <CardContent>
          {associatedRecords.map((r, index) => {
            const { clientType = {} } = r;
            const recordName = getRecordName(r);
            return (
              <Grid container spacing={2} key={index}>
                <Grid item xs={10}>
                  <InputLabel className={classes.inputLabel}>
                    {t("labels.record.documentRequest.viewRequest.recordName")}
                  </InputLabel>
                  <Typography>{recordName}</Typography>
                </Grid>
                <Grid item xs={2}>
                  <InputLabel className={classes.inputLabel}>
                    {t("labels.record.documentRequest.viewRequest.clientType")}
                  </InputLabel>
                  <Typography>{clientType.name}</Typography>
                </Grid>
              </Grid>
            );
          })}
        </CardContent>
      </Card>
    );
  };

  const renderSanitizedRichText = (richText) => {
    return <Markup content={richText} />;
  };

  const renderRecords = () => {
    const { records = [] } = request;
    return records.map((r, index) => (
      <Grid item xs={12} key={index}>
        {renderRecord(r)}
      </Grid>
    ));
  };

  const renderRecord = (record) => {
    const {
      clientType = {},
      documentTypes = {},
      clientUser = {},
      userMessage,
    } = record;
    const { username, userEmail } = clientUser;
    const { coreDocuments = [] } = documentTypes || {};
    const recordName = getRecordName(record);
    if (coreDocuments.length === 0) {
      return;
    }
    return (
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.recordName")}
              </InputLabel>
              <Typography>{recordName}</Typography>
            </Grid>
            <Grid item xs={2}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.clientType")}
              </InputLabel>
              <Typography>{clientType.name}</Typography>
            </Grid>

            {renderDocuments(documentTypes)}
            <Grid item xs={12}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.clientUserName")}
              </InputLabel>
              <Typography>{`${username}(${userEmail})`}</Typography>
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.inputLabel}>
                {t(
                  "labels.record.documentRequest.viewRequest.clientUserMessage",
                )}
              </InputLabel>
              <Box
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  backgroundColor: "rgb(243, 246, 249)",
                  padding: 16,
                }}
              >
                <Paper
                  variant="outlined"
                  square
                  style={{ padding: 8, width: "100%" }}
                >
                  {userMessage ? renderSanitizedRichText(userMessage) : "-"}
                </Paper>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const renderDocuments = (documentTypes) => {
    const { coreDocuments = [], documentNames } = documentTypes || {};
    if (coreDocuments.length > 0) {
      return (
        <Grid container item xs={12}>
          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>
              {t(
                "labels.record.documentRequest.steps.documentSelectionStep.coreDocuments",
              )}
            </InputLabel>
            <Paper component="ul" className={classes.chipArray} elevation={0}>
              {coreDocuments.map((d) => {
                return (
                  <li key={d.id}>
                    <Chip
                      label={d.name}
                      className={classes.docName}
                      size="small"
                    />
                  </li>
                );
              })}
            </Paper>
          </Grid>
          <Grid item xs={12}>
            <InputLabel className={classes.inputLabel}>
              {t(
                "labels.record.documentRequest.steps.documentSelectionStep.documentNames",
              )}
            </InputLabel>
            <Box
              style={{
                display: "flex",
                flexWrap: "wrap",
                backgroundColor: "rgb(243, 246, 249)",
                padding: 16,
              }}
            >
              <Paper
                variant="outlined"
                square
                style={{ padding: 8, width: "100%" }}
              >
                {documentNames ? renderSanitizedRichText(documentNames) : "-"}
              </Paper>
            </Box>
          </Grid>
        </Grid>
      );
    }
  };

  const renderUser = (record) => {
    const { clientUser = {}, userMessage } = record;
    const { username, userEmail } = clientUser;
    return (
      <Card variant="outlined">
        <CardContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <InputLabel className={classes.inputLabel}>
                {t("labels.record.documentRequest.viewRequest.clientUserName")}
              </InputLabel>
              <Typography>{`${username}(${userEmail})`}</Typography>
            </Grid>
            <Grid item xs={12}>
              <InputLabel className={classes.inputLabel}>
                {t(
                  "labels.record.documentRequest.viewRequest.clientUserMessage",
                )}
              </InputLabel>
              <div>
                <Box
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    backgroundColor: "rgb(243, 246, 249)",
                    padding: 16,
                  }}
                >
                  <Paper
                    variant="outlined"
                    square
                    style={{ padding: 8, width: "100%" }}
                  >
                    {userMessage ? renderSanitizedRichText(userMessage) : "-"}
                  </Paper>
                </Box>
              </div>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    );
  };

  const { requestType } = request;
  return (
    <Card variant="outlined">
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={12}>
            {renderHeader()}
          </Grid>
          {requestType === "1" && (
            <Grid item xs={12}>
              {renderAssociatedRecords()}
            </Grid>
          )}
          {requestType === "1" && (
            <Grid item xs={12}>
              {renderUser(request)}
            </Grid>
          )}
          {requestType === "2" && renderRecords()}
        </Grid>
        <altcha-widget
          ref={widgetRef}
          hidefooter={true}
          style={
            altchaLoading
              ? { visibility: "hidden" }
              : {
                  visibility: "visible",
                  display: "flex",
                  flexDirection: "row-reverse",
                }
          }
        ></altcha-widget>
      </CardContent>
      <CardActions disableSpacing>
        <StepActions
          disableNext={!altchaPayload}
          canNext={false}
          canSkip={canSkip}
          canSubmit={true}
          onCancel={handleCancel}
          onSubmit={handleSubmit}
          onBack={handleBack}
        />
      </CardActions>
    </Card>
  );
};

export default withStyles(styles, { withTheme: true })(ReviewRequestStep);
